// Organization - the vertical switcher (the sales-demo weapon) + report branding.
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Grid, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import lpsService from '../services/lpsService';

const VERTICAL_META = {
  general:    { icon: '🏢', label: 'General',            blurb: 'Meetings, decisions, action items.' },
  healthcare: { icon: '🏥', label: 'Hospital',           blurb: 'Clinical sections, subject IDs, ISO 27799 posture.' },
  clinic:     { icon: '🩺', label: 'Clinic / Therapy',   blurb: 'Session reports for private practices.' },
  legal:      { icon: '⚖️', label: 'Legal',              blurb: 'Commitments & positions, deposition-style records.' },
  hr:         { icon: '👥', label: 'HR',                 blurb: 'Interviews, hearings, agreements.' },
  hls:        { icon: '🛡️', label: 'HLS / Defense',     blurb: 'Risks, motives, air-gapped operation.' },
};

export default function LpsOrgPage() {
  const [org, setOrg] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => { lpsService.getOrg().then(setOrg); }, []);
  if (!org) return <Typography sx={{ p: 4 }}>Loading…</Typography>;

  const save = async (update) => {
    await lpsService.updateOrg(update);
    setOrg({ ...org, ...update });
    setToast('Saved — applies to new reports immediately');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>Organization</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The industry pack drives report sections, terminology and dashboards.
      </Typography>

      <Typography variant="overline" color="primary" fontWeight={700}>Industry pack</Typography>
      <Grid container spacing={2} sx={{ mb: 4, mt: 0 }}>
        {(org.verticals || []).map((v) => {
          const meta = VERTICAL_META[v] || { icon: '❔', label: v, blurb: '' };
          const active = org.vertical === v;
          return (
            <Grid item xs={6} sm={4} md={2} key={v}>
              <Card
                variant={active ? 'elevation' : 'outlined'}
                sx={{
                  cursor: 'pointer', textAlign: 'center', py: 1,
                  border: active ? 2 : 1, borderColor: active ? 'primary.main' : 'divider',
                }}
                onClick={() => save({ vertical: v })}
              >
                <CardContent>
                  <Typography fontSize={30}>{meta.icon}</Typography>
                  <Typography fontWeight={700}>{meta.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{meta.blurb}</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="overline" color="primary" fontWeight={700}>Report branding</Typography>
      <Card variant="outlined" sx={{ maxWidth: 560, mt: 1 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField label="Organization display name" defaultValue={org.display_name}
              onBlur={(e) => e.target.value !== org.display_name && save({ display_name: e.target.value })} />
            <TextField label="Report footer" defaultValue={org.report_footer || ''} multiline
              onBlur={(e) => e.target.value !== (org.report_footer || '') && save({ report_footer: e.target.value })} />
            <Typography variant="caption" color="text.secondary">
              Logo upload lands with the cloud deployment build.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="overline" color="primary" fontWeight={700} sx={{ mt: 4, display: 'block' }}>
        Enterprise SSO (OIDC)
      </Typography>
      <Card variant="outlined" sx={{ maxWidth: 560, mt: 1 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Connect your identity provider (Okta, Entra ID, Auth0, Keycloak…).
              Users sign in with your IdP; accounts are provisioned automatically
              with least-privilege roles.
            </Typography>
            <TextField label="Issuer URL" placeholder="https://login.example.com"
              defaultValue={org.sso?.issuer || ''}
              onBlur={(e) => save({ sso: { ...org.sso, issuer: e.target.value } })} />
            <TextField label="Client ID" defaultValue={org.sso?.client_id || ''}
              onBlur={(e) => save({ sso: { ...org.sso, client_id: e.target.value } })} />
            <TextField label="Client Secret" type="password"
              defaultValue={org.sso?.client_secret || ''}
              onBlur={(e) => save({ sso: { ...org.sso, client_secret: e.target.value } })} />
            <TextField label="Button label" placeholder="Continue with Acme SSO"
              defaultValue={org.sso?.display_name || ''}
              onBlur={(e) => save({ sso: { ...org.sso, display_name: e.target.value } })} />
            <Typography variant="caption" color="text.secondary">
              Redirect URI to register at your IdP: {window.location.origin}/auth/sso/callback
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}
